import { DynamicFieldsAPI, FetchAPI, FieldAPI, FieldsetAPI, FormAPI, LocatorAPI, LocatorFieldAPI, Metadata, ToastService } from '@philips-internal/oswald-ui-components';
import { Inject } from '@odinjs/odin';
import Controller, { OnAfterFetch, OnBeforeSubmit, OnCancel, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { DeviceEventActionAliases, FragmentNames } from '../../../codegen';
import BaseController from '../../BaseController';
import moment from 'moment';
import { ISO_FULL_DATE_FORMAT } from '../../../constants/date-utils';
import { HttpService, } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../constants/endpoints';
import { ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import {DetailEventType, EventType, PersonType, SectorType} from './device-event-action-types';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HealthProfessionalUser } from '../../../app/authentication';
import { AbortableSubmitEvent, FetchEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { LocatorTypes } from '../../locator/locator-controller';
import { ActionType, DevicesViewerType } from '../commons/register-device';
import { Value } from '@philips-internal/oswald-ui-core';
import { Validator } from '@philips-internal/oswald-ui-core/lib/services/model-service';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { DevicesDynamicForm } from '../commons/devices-dynamic-form';

enum DeviceType {
  DEVICE_TYPE = 'E',
}

@Controller({ component: DeviceEventActionAliases.LAYOUT_EVENT, fragment: FragmentNames.DEVICE_EVENT_ACTION })
class DeviceEventActionController extends BaseController {

  @Inject(DeviceEventActionAliases.FORM_EVENT)
  formEventApi!: FormAPI;

  @Inject(DeviceEventActionAliases.FETCH_DETAIL)
  fetchDeatilApi!: FetchAPI;

  @Inject(DeviceEventActionAliases.FETCH_PROFESSIONAL)
  fetchProfessionalApi!: FetchAPI;

  @Inject(DeviceEventActionAliases.LOCATOR_FIELD_PROFESSIONAL)
  locatorFieldApi!: LocatorFieldAPI;

  @Inject(DeviceEventActionAliases.FIELDSET_DYNAMIC_FIELDS)
  fieldsetDynamicAPI!: FieldsetAPI;

  @Inject(DeviceEventActionAliases.DYNAMIC_FIELDS)
  dynamicFieldAPI!: DynamicFieldsAPI;

  @Inject(DeviceEventActionAliases.FIELD_EVENT)
  fieldEventAPI!: FieldAPI;

  private returnDevicesList(): void {
    applicationContext.popNavigationItem();
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'device-remove-validation',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private submitEvent(body: Record<string, Value>): void {
    HttpService.post(Endpoint.DEVICES_REGISTER, undefined, {body})
      .then(() => {
        this.showToast('patient-evolution.release-evolution', ToastLevel.SUCCESS);
        this.returnDevicesList();
      })
      .catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      })
  }

  @OnMount(DeviceEventActionAliases.DYNAMIC_FIELDS)
  handleMountDynamicFields(): void {
    const dynamicFields = this.context.get('dynamicFields') as Metadata;
    this.dynamicFieldAPI.setMetadata(dynamicFields);
  }

  private handleDynamicFields(): void {
    const {deviceSequence} = this.historyService.getCurrentRouteParams();
    const deviceType = DeviceType.DEVICE_TYPE;

    DevicesDynamicForm.handleDynamicFields(this.fieldsetDynamicAPI, deviceSequence, deviceType, this.context);
  }

  @OnBeforeSubmit({ target: DeviceEventActionAliases.FORM_EVENT })
  handleBeforeSubmitForm(e: AbortableSubmitEvent): void {
    e.abort();
    const formInfos = DevicesDynamicForm.createFormPattern(e);
    const formData = this.formEventApi.getRecord();
    const {patientEncounterDeviceId, encounterId} = this.historyService.getCurrentRouteParams();
    const devSeq = this.context.get('deviceSequence');
    const person = formData.person as PersonType;
    const formatDateTime = moment(formData.date as string).utc().format(ISO_FULL_DATE_FORMAT);
    const observation = formData.description as string;
    const detail = formData.detail as DetailEventType;
    const sector = formData.sector as SectorType;
    const event = formData.event as EventType;
    const immediateAction = formData.immediateAction as string;
    const action = formData.withdrawnAction as ActionType;
    const body = {
      actionDeviceSequence: action?.internalProcedureSequence,
      classificationEventSequence: detail?.detailingSequence,
      deviceAttributes: formInfos,
      deviceSequence: devSeq,
      encounterDepartmentCode: sector?.unitCode,
      encounterId: encounterId as number,
      eventDescription: observation,
      eventSequence: event?.eventSequence,
      immediateActionDescription: immediateAction,
      optionStatus: DeviceType.DEVICE_TYPE,
      professionalCode: person.personCode,
      registerDate: formatDateTime,
      registerSequence: patientEncounterDeviceId as number,
      releasedStatus:'S',
      removeStatusDischarge: 'N',
      successStatus: 'S',
    }
    this.submitEvent(body);
  }

  private getDevices(): void {
    const { patientEncounterDeviceId, patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const dateField = `${moment().format('L')} ${moment().format('LTS')}`;
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const person = {
      personCode: user?.physicalPersonId,
      personName: user?.name,
    }

    HttpService.get(Endpoint.GET_DEVICES_VIEWER, {
      encounterId,
      patientEncounterDeviceId,
      patientId
    }).then((response) => {
      const res = response.body as DevicesViewerType;
      const deviceDescription = res.deviceDescription;
      const deviceSequence = res.deviceSequence;
      this.formEventApi.setData({ ...res, dateField, deviceDescription, person });
      this.handleDynamicFields();
      this.context.set({
        deviceSequence: deviceSequence,
        encounterId: encounterId,
        locator: LocatorTypes.PROFESSIONAL_PERSON,
        patientId: patientId,
      });
    });
  }

  @OnMount({ target: DeviceEventActionAliases.FORM_EVENT })
  handleMountLayoutEvent(): void {
    InformationBarUtils.collapsedBar = true;
    this.getDevices();
  }

  @OnMount({ target: DeviceEventActionAliases.LOCATOR_PROFESSIONAL })
  handleMountProfessionalLocator(event: MountEvent): void {
    const locatorApi = event.target as unknown as LocatorAPI;
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    user && locatorApi.setValue({
      personCode: user.physicalPersonId,
      personName: user.name,
    })
  }

  @OnChange({ target: DeviceEventActionAliases.FIELD_EVENT })
  handleChangeFieldState(event: ChangeEvent<EventType>): void {
    if (event.value) {
      this.fetchDeatilApi.setParameters({ eventSequence: event.value.eventSequence });
      this.fetchDeatilApi.forceFetch();
    }
  }

  @OnCancel({ target: DeviceEventActionAliases.FORM_EVENT })
  handleCancelFormEvent(): void {
    this.returnDevicesList();
  }

  @OnAfterFetch({ target: DeviceEventActionAliases.FETCH_EVENT })
  handleAfterFetch(event: FetchEvent): void {
    const validator: Validator = { name: 'required', options: {}};
    const resArray = event.data as Record<string, string>[];
    if (event && resArray.length > 0) {
      this.fieldEventAPI.addValidator(validator);
      this.fieldEventAPI.validate();
      return;
    }
    this.fieldEventAPI.removeValidator('required');
  }

}

export default DeviceEventActionController;
